/**
 * estoque.js — Renderiza o grid "Nosso Estoque".
 *
 * Responsabilidades:
 *  - Buscar todos os veículos não-destaque via API (uma única vez).
 *  - Expor o array bruto `_todosVeiculos` para o filtro usar.
 *  - Renderizar um subconjunto de veículos no grid (com paginação).
 *  - Exibir estado vazio e estado de erro.
 */

import { fetchTodosVeiculos } from '../services/veiculos.js';
import { fetchMarcas }        from '../services/marcas.js';

const PAGE_SIZE = 8; // cards visíveis por "página"

/** Array completo de veículos vindos da API (compartilhado com filtro.js) */
export let _todosVeiculos = [];

/** Array atual a exibir (pode ser subconjunto filtrado) */
let _veiculosFiltrados = [];

/** Índice do próximo batch a exibir no "Carregar mais" */
let _pagina = 0;

// ── Referências DOM ────────────────────────────────────────────
const getGrid     = () => document.getElementById('stock-grid');
const getLoadWrap = () => document.getElementById('load-more-wrap');
const getLoadBtn  = () => document.getElementById('btn-load-more');

// ── Mapeamento API → display ───────────────────────────────────

function mapApiCar(v, marcasMap) {
  const preco = v.preco_venda?.toLocaleString('pt-BR', { minimumFractionDigits: 0 }) ?? '—';
  const brandName = marcasMap?.get(v.marca_uuid) ?? v.marcas?.nome ?? v.marca_nome ?? '';
  return {
    id:        v.id,
    slug:      v.slug,
    name:      `${v.nome ?? ''} ${v.modelo ?? ''}`.trim(),
    price:     `R$ ${preco}`,
    priceRaw:  v.preco_venda ?? 0,
    year:      String(v.ano_fabricacao ?? v.ano_modelo ?? ''),
    yearNum:   Number(v.ano_fabricacao ?? v.ano_modelo ?? 0),
    cambio:    v.cambio ?? '',
    combustivel: v.combustivel ?? '',
    tipo:      v.tipo ?? '',
    marca:     brandName.toLowerCase(),
    marcaNome: brandName,
    img:       v.foto_capa ?? 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?q=80&w=800&auto=format&fit=crop',
  };
}

// ── HTML do card ───────────────────────────────────────────────

function cardStockHTML(car) {
  const link = `/veiculo?slug=${car.slug || car.id}`;
  return `
    <div class="card-stock-wrap" id="card-wrap-${car.id}">
      <a href="${link}" class="card-stock" id="card-stock-${car.id}">
        <div class="card-stock-img-wrap">
          <img
            src="${car.img}"
            alt="${car.name}"
            class="card-stock-img"
            loading="lazy"
          />
        </div>
        <div class="card-stock-body">
          <h3 class="card-stock-name">${car.name}</h3>
          <p class="card-stock-meta">${car.year}${car.cambio ? ' · ' + car.cambio : ''}</p>
          <p class="card-stock-price">${car.price}</p>
        </div>
      </a>
      <button class="btn-comparar" 
        data-car-id="${car.id}" 
        data-car-slug="${car.slug || car.id}"
        data-car-img="${car.img}"
        data-car-name="${car.name}"
        data-car-price="${car.price}"
        aria-label="Comparar ${car.name}">
        <svg class="comparar-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h5v5"/><path d="M8 3H3v5"/><path d="M21 3 14 10"/><path d="M3 3l7 7"/><path d="M16 21h5v-5"/><path d="M8 21H3v-5"/><path d="M21 21l-7-7"/><path d="M3 21l7-7"/></svg>
        <span class="comparar-label">Comparar</span>
      </button>
    </div>
  `;
}

// ── Renderização paginada ──────────────────────────────────────

/**
 * Renderiza o próximo lote de cards no grid.
 * Se `reset` for true, limpa o grid e volta ao início.
 */
export function renderizarLote(reset = false) {
  const grid = getGrid();
  if (!grid) return;

  if (reset) {
    _pagina = 0;
    grid.innerHTML = '';
  }

  const inicio = _pagina * PAGE_SIZE;
  const lote   = _veiculosFiltrados.slice(inicio, inicio + PAGE_SIZE);

  if (lote.length === 0 && _pagina === 0) {
    grid.innerHTML = `
      <div class="estoque-vazio" id="estoque-vazio">
        <svg width="40" height="40" fill="none" stroke="currentColor" stroke-width="1" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <p>Nenhum veículo encontrado com os filtros aplicados.</p>
        <button class="btn btn-outline btn-sm" id="btn-limpar-filtro-vazio">Limpar filtros</button>
      </div>
    `;
    atualizarLoadMore();
    return;
  }

  grid.insertAdjacentHTML('beforeend', lote.map(cardStockHTML).join(''));
  _pagina++;

  atualizarLoadMore();
}

/** Exibe ou oculta o botão "Carregar mais" e o contador. */
function atualizarLoadMore() {
  const wrap    = getLoadWrap();
  const btn     = getLoadBtn();
  if (!wrap || !btn) return;

  const exibidos = Math.min(_pagina * PAGE_SIZE, _veiculosFiltrados.length);
  const total    = _veiculosFiltrados.length;
  const temMais  = exibidos < total;

  wrap.hidden = total === 0;

  const contador = wrap.querySelector('.estoque-contador');

  if (temMais) {
    btn.hidden = false;
    if (contador) contador.textContent = `${exibidos} de ${total} veículos`;
  } else {
    btn.hidden = true;
    if (contador) contador.textContent = `${total} veículos no estoque`;
  }
}

// ── API pública ────────────────────────────────────────────────

/**
 * Aplica um novo subconjunto filtrado e re-renderiza do zero.
 * Chamado por filtro.js sempre que os filtros mudam.
 * @param {Array} lista - Subconjunto de `_todosVeiculos` já filtrado.
 */
export function aplicarFiltro(lista) {
  _veiculosFiltrados = lista;
  renderizarLote(true);
}

// ── Inicialização ──────────────────────────────────────────────

/**
 * Ponto de entrada. Busca os dados, popula `_todosVeiculos` e renderiza.
 * @returns {Promise<Array>} Array completo de veículos mapeados.
 */
export async function renderEstoque() {
  const grid = getGrid();
  if (!grid) return [];

  // Skeletons enquanto carrega
  grid.innerHTML = Array(PAGE_SIZE).fill(
    '<div class="skeleton skeleton-card"></div>'
  ).join('');

  // Busca veículos e marcas em paralelo
  const [veiculosRes, marcasRes] = await Promise.all([
    fetchTodosVeiculos().catch(() => ({ data: null, error: true })),
    fetchMarcas().catch(() => ({ data: null }))
  ]);

  const veiculos = veiculosRes.data;
  const error = veiculosRes.error;
  const marcas = marcasRes?.data;

  // Cria um mapa id -> nome da marca
  const marcasMap = new Map();
  if (marcas) {
    marcas.forEach(m => marcasMap.set(m.id, m.nome));
  }

  const cars = (veiculos && veiculos.length > 0) ? veiculos.map(v => mapApiCar(v, marcasMap)) : [];

  if (error && !cars.length) {
    console.warn('[estoque] Nenhum dado retornado ou API indisponível.');
    grid.innerHTML = `
      <p class="estoque-erro" style="grid-column:1/-1;text-align:center;color:var(--neutral-500);padding:3rem 0;">
        Erro ao carregar o estoque. Tente novamente em instantes.
      </p>
    `;
    return [];
  }

  _todosVeiculos     = cars;
  _veiculosFiltrados = cars;

  renderizarLote(true);

  // Botão "Carregar mais"
  const btn = getLoadBtn();
  if (btn) {
    btn.addEventListener('click', () => renderizarLote(false));
  }

  return cars;
}
